import { api } from "../axios";
import { expireSession } from "./expireSession.web";
import { storeAuthToken } from "./SessionStorage.web";
import { RENEWED_TOKEN_HEADER } from "./sessionConfig";
import { isSessionExpiredMessage } from "./sessionMessages";

const carriedToken = (headers: any): boolean =>
  Boolean(headers?.token ?? headers?.get?.("token"));

const mentionsExpiredSession = (body: any): boolean => {
  if (!body) {
    return false;
  }

  return isSessionExpiredMessage(JSON.stringify(body.errors ?? body));
};

const isSessionExpired = (
  status: number,
  headers: any,
  body: any
): boolean => {
  // These messages only come from the server side session guard, never from a
  // sign in attempt, so they are enough on their own. This also covers the
  // request that reaches the API with no token header at all.
  if (mentionsExpiredSession(body)) {
    return true;
  }

  // A bare 401 on a request that carried no token is a failed sign in, not an
  // expired session, and must not sign out whoever is already in.
  return status === 401 && carriedToken(headers);
};

const handleRenewedToken = (renewedToken?: string | null) => {
  if (renewedToken) {
    storeAuthToken(renewedToken);
  }
};

function installFetchInterceptor(): void {
  const previousFetch = window.fetch;

  window.fetch = (async (input: any, init?: any) => {
    const response = await previousFetch(input, init);

    if (!response || typeof response.status !== "number") {
      return response;
    }

    handleRenewedToken(response.headers?.get?.(RENEWED_TOKEN_HEADER));

    if (response.ok) {
      return response;
    }

    let body = null;

    try {
      body = await response.clone().json();
    } catch {
      body = null;
    }

    if (isSessionExpired(response.status, init?.headers, body)) {
      expireSession();
    }

    return response;
  }) as typeof window.fetch;
}

// The react-query layer talks through axios, which uses XHR and therefore never
// reaches the fetch interceptor above.
function installAxiosInterceptor(): void {
  api.interceptors.response.use(
    response => {
      handleRenewedToken(response.headers?.[RENEWED_TOKEN_HEADER]);
      return response;
    },
    error => {
      const response = error?.response;

      if (
        response &&
        isSessionExpired(
          response.status,
          error?.config?.headers,
          response.data
        )
      ) {
        expireSession();
      }

      return Promise.reject(error);
    }
  );
}

export function installSessionInterceptors(): void {
  installFetchInterceptor();
  installAxiosInterceptor();
}
